// dependencies
import React from "react";

// components
import { Chip, ICellRendererParams, Link, PYColumnDef, Text } from "@performyard/py-components";

// types
import type { ReviewCycleListItem } from "../types";

const NameCellRenderer = (params: ICellRendererParams<ReviewCycleListItem>) => {
	const reviewCycle = params.data;
	if (!reviewCycle) return null;

	const { name, _id } = reviewCycle;

	return (
		<div className="flex h-full items-center">
			<Link href={`/review-cycle/${_id}/`}>
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{name}
				</Text>
			</Link>
		</div>
	);
};

const ChipsCellRenderer = (params: ICellRendererParams<ReviewCycleListItem>) => {
	const reviewCycle = params.data;
	if (!reviewCycle) return null;

	const hasEasyAdd = (reviewCycle.easyAddConfigCount ?? 0) > 0;
	const isAutomated =
		reviewCycle.type === "automated" && ["active", "scheduled"].includes(reviewCycle.automationStatus ?? "");

	return (
		<div className="flex h-full items-center justify-end gap-1">
			{isAutomated && <Chip color="green" label="Automated" size={24} />}
			{hasEasyAdd && <Chip color="primary" label="360 Enabled" size={24} />}
		</div>
	);
};

const StatusCellRenderer = (params: ICellRendererParams<ReviewCycleListItem>) => {
	const reviewCycle = params.data;
	if (!reviewCycle) return null;

	const { status, progress } = reviewCycle;
	const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
	const statusText = `${statusLabel} (${progress.completedReviews}/${progress.totalReviews})`;

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
				{statusText}
			</Text>
		</div>
	);
};

const getReviewCycleTableColsRefresh = (): PYColumnDef<ReviewCycleListItem>[] => {
	return [
		{
			field: "name",
			headerName: "Name",
			sortable: true,
			resizable: false,
			width: 456,
			cellRenderer: NameCellRenderer,
		},
		{
			colId: "chips",
			headerName: "",
			sortable: false,
			resizable: false,
			width: 238,
			cellRenderer: ChipsCellRenderer,
		},
		{
			field: "startDate",
			headerName: "Start Date",
			sortable: true,
			resizable: false,
			width: 180,
			type: "date",
			cellRendererParams: {
				dateFormat: "MMM dd, yyyy h:mmaaa",
			},
		},
		{
			field: "status",
			headerName: "Status",
			sortable: true,
			resizable: false,
			width: 220,
			cellRenderer: StatusCellRenderer,
		},
	];
};

export default getReviewCycleTableColsRefresh;
